import { useState, useEffect } from 'react';

// Toast notification type
export type ToastType = 'success' | 'error' | 'info' | 'warning';

// Toast item structure
interface ToastItem {
    id: string;
    message: string;
    type: ToastType;
    persistent: boolean;
    timestamp: number;
}

// Create a global event system for showing toasts
type ToastEvent = {
    message: string;
    type: ToastType;
    persistent?: boolean; // Optional parameter to control toast persistence
};

// Create a custom event name
const TOAST_EVENT = 'show-toast';

// Generate a unique ID for each toast
const generateId = () => `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Custom event to show toast
export const showToast = (message: string, type: ToastType = 'info', persistent?: boolean) => {
    // If type is 'error', make it persistent by default unless explicitly set to false
    const shouldPersist = persistent !== undefined ? persistent : type === 'error';

    const event = new CustomEvent<ToastEvent>(TOAST_EVENT, {
        detail: { message, type, persistent: shouldPersist }
    });

    document.dispatchEvent(event);
};

const Toast = () => {
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    // Remove a specific toast by ID
    const removeToast = (id: string) => {
        setToasts((currentToasts) => currentToasts.filter((toast) => toast.id !== id));
    };

    useEffect(() => {
        const handleToast = (event: Event) => {
            const customEvent = event as CustomEvent<ToastEvent>;

            const newToast: ToastItem = {
                id: generateId(),
                message: customEvent.detail.message,
                type: customEvent.detail.type,
                persistent: !!customEvent.detail.persistent,
                timestamp: Date.now()
            };

            setToasts((currentToasts) => [...currentToasts, newToast]);

            // Only auto-hide if not persistent
            if (!customEvent.detail.persistent) {
                setTimeout(() => {
                    removeToast(newToast.id);
                }, 3000);
            }
        };

        // Add event listener for the custom toast event
        document.addEventListener(TOAST_EVENT, handleToast);

        // Clean up event listener
        return () => {
            document.removeEventListener(TOAST_EVENT, handleToast);
        };
    }, []);

    // Get the appropriate class based on the toast type
    const getToastClass = (type: ToastType) => {
        switch (type) {
            case 'success':
                return 'bg-success text-white';
            case 'error':
                return 'bg-danger text-white';
            case 'warning':
                return 'bg-warning text-black';
            case 'info':
            default:
                return 'bg-info text-white';
        }
    };

    if (toasts.length === 0) return null;

    return (
        <div className="fixed bottom-5 right-5 z-[99999]">
            {toasts.map((toast) => (
                <div
                    aria-atomic="true"
                    aria-live="assertive"
                    className={`${getToastClass(toast.type)} mb-2 rounded shadow-lg min-w-[250px] overflow-hidden`}
                    key={toast.id}
                    role="alert"
                >
                    <div className="flex items-center justify-between">
                        <div className="p-3">{toast.message}</div>
                        <button
                            aria-label="Close"
                            className="p-3 text-white hover:bg-black hover:bg-opacity-10"
                            type="button"
                            onClick={() => removeToast(toast.id)}
                        >
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    clipRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    fillRule="evenodd"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default Toast;
